import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Heading } from '@/components/ui/shared/heading';
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useUserState } from '@/components/context/UserContext';
import ComplexBuyerInfo from '../Components/ComplexBuyerInfo';
import { useCartData } from '../../CartPage/context/CartDataContext';
import RedeemCodeSection from '@/v2/components/RedeemCodeSection';
import { Separator } from '@radix-ui/react-dropdown-menu';
import useFetch from 'use-http';
import { DiscountApplicationStatus } from '@/v2/constants/discountApplicationStatus';

export function ComplexItemInfo({ price }) {
  const userState = useUserState();
  const [repriced, setRepriced] = React.useState(null);
  const [discountCodeItem, setDiscountCodeItem] = React.useState(null);
  const [couponItems, setCouponItems] = React.useState([]);
  const { complexPrice, complex, setComplexPrice } = useCartData();

  // const { post: checkDiscountCode, response: checkDiscountCodeResponse } =
  //   useFetch('/DiscountManagement/GetDiscount');

  // const { post: checkCoupon, response: checkCouponResponse } = useFetch(
  //   '/CouponManagement/CheckCoupon',
  // );

  const { post: checkPrice, response: checkPriceResponse } = useFetch(
    '/DiscountManagement/GetDiscountsComplex',
  );

  const isLoggedIn = !!userState?.isLoggedIn;
  let complexService = complex?.service;
  const svc = React.useMemo(() => {
    if (complexService && typeof complexService === 'object')
      return complexService;
    if (!complexService) return undefined;
  }, [complexService]);

  async function onApplyCoupon(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, errorText: 'Įveskite kodą' };
    if (!svc?.serviceId)
      return { ok: false, errorText: 'Paslauga nepasirinkta' };
    if (couponItems.some((x) => x.id === trimmed)) return { ok: true };

    try {
      const res = await checkPrice({
        serviceId: svc.serviceId,
        discountCode: discountCodeItem?.id,
        departmentId: complexPrice.departmentId,
        currentPriceId: complexPrice.priceId,
        basePriceId: complexPrice.basePriceId ?? complexPrice.priceId,
        coupons: [...couponItems.map((x) => x.id), trimmed],
      });

      if (!res?.couponApplied)
        return { ok: false, errorText: 'Kuponas nerastas' };

      setComplexPrice((prev) => ({
        ...prev,
        price: res.price,
        discountCode: discountCodeItem?.id,
        priceId: res.appliedPriceId,
        basePriceId: res.basePriceId,
        discountPrice: true,
        couponApplied: res.appliedCouponNumbers?.length > 0,
        appliedCoupons: res.appliedCouponNumbers,
      }));
      // preview repricing WITH this new coupon before committing UI state
      const nextCoupons = [...couponItems.map((c) => c.id), trimmed];

      const item = { id: trimmed, rightText: '' };
      setCouponItems((prev) => [...prev, item]);
      return { ok: true, item };
    } catch {
      return { ok: false, errorText: 'Įvyko klaida bandant pritaikyti kuponą' };
    }
  }

  async function onRemoveCoupon(id) {
    const nextCoupons = couponItems.filter((c) => c.id !== id).map((c) => c.id);
    const res = await checkPrice({
      serviceId: svc.serviceId,
      discountCode: discountCodeItem?.id,
      departmentId: complexPrice.departmentId,
      currentPriceId: complexPrice.priceId,
      basePriceId: complexPrice.basePriceId ?? complexPrice.priceId,
      coupons: [...nextCoupons],
    });

    setComplexPrice((prev) => ({
      ...prev,
      price: res.price,
      discountCode: discountCodeItem?.id,
      priceId: res.appliedPriceId,
      basePriceId: res.basePriceId,
      discountPrice: true,
      couponApplied: res.appliedCouponNumbers?.length > 0,
      appliedCoupons: res.appliedCouponNumbers,
    }));
    setCouponItems((prev) => prev.filter((x) => x.id !== id));
    return { ok: true };
  }

  async function onApplyDiscount(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, errorText: 'Įveskite kodą' };
    if (!svc?.serviceId)
      return { ok: false, errorText: 'Paslauga nepasirinkta' };
    if (discountCodeItem?.id === trimmed) return { ok: true };

    try {
      const res = await checkPrice({
        discountCode: trimmed,
        serviceId: svc.serviceId,
        departmentId: complexPrice.departmentId,
        currentPriceId: complexPrice.priceId,
        basePriceId: complexPrice.priceId,
        coupons: [...couponItems.map((x) => x.id)],
      });

      if (
        res?.discountApplicationStatus !== DiscountApplicationStatus.Undefined
      ) {
        if (
          res?.discountApplicationStatus ===
            DiscountApplicationStatus.NotFound ||
          res?.discountApplicationStatus ===
            DiscountApplicationStatus.NotYetActive
        )
          return { ok: false, errorText: 'Nuolaidos kodas nerastas.' };
        if (
          res?.discountApplicationStatus === DiscountApplicationStatus.Expired
        )
          return { ok: false, errorText: 'Nuolaidos kodas nebegalioja.' };
        if (
          res?.discountApplicationStatus ===
          DiscountApplicationStatus.NotApplicable
        )
          return {
            ok: false,
            errorText: 'Pasirinktai paslaugai nuolaida netaikoma.',
          };

        if (
          res?.discountApplicationStatus !==
          DiscountApplicationStatus.Applicable
        )
          return {
            ok: false,
            errorText: 'Nepavyko pritakyti nuolaidos kodo.',
          };
      }

      setComplexPrice((prev) => ({
        ...prev,
        price: res.price,
        discountCode: trimmed,
        priceId: res.appliedPriceId,
        basePriceId: res.basePriceId,
        discountPrice: true,
        couponApplied: res.appliedCouponNumbers?.length > 0,
        appliedCoupons: res.appliedCouponNumbers,
      }));
      const item = { id: trimmed, rightText: '' };
      setDiscountCodeItem(item);
      return { ok: true, item };
    } catch {
      return {
        ok: false,
        errorText: 'Įvyko klaida bandant pritaikyti nuolaidos kodą',
      };
    }
  }

  async function onRemoveDiscount(id) {
    // reprice WITHOUT discount first

    const res = await checkPrice({
      discountCode: null,
      serviceId: svc.serviceId,
      departmentId: complexPrice.departmentId,
      currentPriceId: complexPrice.priceId,
      basePriceId: complexPrice.basePriceId,
      coupons: [...couponItems.map((x) => x.id)],
    });

    setComplexPrice((prev) => ({
      ...prev,
      discountCode: null,
      price: res.price,
      priceId: res.appliedPriceId,
      basePriceId: res.basePriceId,
      discountPrice: true,
      couponApplied: res.appliedCouponNumbers?.length > 0,
      appliedCoupons: res.appliedCouponNumbers,
    }));

    if (discountCodeItem?.id === id) setDiscountCodeItem(null);
    return { ok: true };
  }

  const displayLinePrice = repriced
    ? Number(repriced.servicePrice ?? 0) // ★ server value
    : 10;

  const displayFooter =
    repriced && repriced.total != null
      ? Number(repriced.total) // ★ server value
      : displayLinePrice;
  return (
    <Card className="w-full max-w-md rounded-lg shadow-sm">
      <CardHeader>
        <h2 className="text-xl font-bold text-[#1F4B81]">
          Apmokėjimo informacija
        </h2>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Product Row */}
        <div className="flex justify-between items-start mb-2.5">
          <div>
            <span className="text-gray-700 block">
              {complex?.service?.title}
            </span>
            {complex?.service?.serviceParts?.length > 0 && (
              <div className="mt-1 flex flex-col gap-0.5">
                {complex.service.serviceParts.map((part, index) => {
                  const hasObjectServices = part.objectServices?.length > 0;
                  const displayText = hasObjectServices
                    ? part.objectServices.map((obj) => obj.name).join(', ')
                    : part.amount
                    ? `${part.name} - ${part.amount}`
                    : part.name;

                  return (
                    <span key={index} className="text-xs text-gray-400">
                      {displayText}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {complexPrice?.price && (
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-primary font-bold">
              {/* Current price */}
              <span className="whitespace-nowrap">{complexPrice.price} €</span>

              {/* Original price (crossed) */}
              {complexPrice.originalPrice &&
                complexPrice.originalPrice > complexPrice.price && (
                  <span className="text-gray-400 line-through font-normal whitespace-nowrap">
                    {complexPrice.originalPrice} €
                  </span>
                )}
            </div>
          )}
        </div>

        {complexPrice && (
          <>
            <RedeemCodeSection
              singleUse
              title="Nuolaidos kodas"
              placeholder="Įveskite nuolaidos kodą"
              items={discountCodeItem ? [discountCodeItem] : []}
              onApply={onApplyDiscount}
              onRemove={onRemoveDiscount}
            />
            <Separator className="my-2" />

            {/* --- Multi-use: Kuponai --- */}
            <RedeemCodeSection
              title="Kuponai"
              placeholder="Įveskite kupono kodą"
              items={couponItems}
              onApply={onApplyCoupon}
              onRemove={onRemoveCoupon}
            />
          </>
        )}

        <Separator className="my-2" />
      </CardContent>

      <CardFooter className="flex justify-between items-center">
        <span className="font-medium">Iš viso su PVM</span>
        {complexPrice?.price && (
          <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-primary font-bold">
            {/* Current total */}
            <span className="whitespace-nowrap">{complexPrice.price} €</span>

            {/* Original total (crossed) */}
            {complexPrice.originalPrice &&
              complexPrice.originalPrice > complexPrice.price && (
                <span className="text-gray-400 line-through font-normal whitespace-nowrap">
                  {complexPrice.originalPrice} €
                </span>
              )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
